/**
 * @file 常用util
 * @author mengchen <mengchen@babytree-inc.com>
 */
import { checkLogin } from '@bbtfe/common-tools-lib';
const loadPromises = {};

export function loadScript(src, opts = {}) {
  if (loadPromises[src]) {
    return loadPromises[src];
  }
  const promise = (loadPromises[src] = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = opts.type || 'text/javascript';
    script.charset = opts.charset || 'utf8';
    script.async = 'async' in opts ? !!opts.async : true;
    script.src = src;
    if (opts.attrs) {
      for (const attr in opts.attrs) {
        script.setAttribute(attr, opts.attrs[attr]);
      }
    }
    let loaded = false;
    script.onload = script.onreadystatechange = function (a, b, c, d) {
      if (
        (script.readyState &&
          script.readyState !== 'complete' &&
          script.readyState !== 'loaded') ||
        loaded
      ) {
        return false;
      }
      script.onload = script.onreadystatechange = null;
      loaded = true;
      resolve();
    };
    script.onerror = function () {
      script.onload = script.onreadystatechange = null;
      reject();
    };
    const head = document.getElementsByTagName('head')[0];
    head.insertBefore(script, head.firstChild);
  }));
  return promise;
}

export function loadCSSCode(code) {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.rel = 'stylesheet';
  // for Chrome Firefox Opera Safari
  style.appendChild(document.createTextNode(code));
  // for IE
  // style.styleSheet.cssText = code;
  const head = document.getElementsByTagName('head')[0];
  head.appendChild(style);
}

export function loadCSS(href) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  const head = document.getElementsByTagName('head')[0];
  head.appendChild(link);
}

export function loadImage(src) {
  if (loadPromises[src]) {
    return loadPromises[src];
  }
  const promise = (loadPromises[src] = new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.src = src;
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function (e) {
      img.onload = null;
      reject(e);
    };
  }));
  return promise;
}

export function getPromise(timeout = 0) {
  return new Promise((resolve) => {
    const timerId = window.setTimeout(() => {
      window.clearTimeout(timerId);
      resolve();
    }, timeout);
  });
}

export function imageToBase64(url, outputFormat) {
  return new Promise(resolve => {
    let canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.height = img.height;
      canvas.width = img.width;
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL(outputFormat || 'image/png');
      resolve(dataURL);
      canvas = null;
    };
    img.src = url;
  });
}

const RegExpMap = {
  Edge: /Edge/i,
  Wechat: /MicroMessenger/,
  Weibo: /Weibo/,
  QQ: /\sQQ\//,
  MQQBrowser: /MQQBrowser/i,
  Qzone: /\sQzone\//,
  Mobile: /Mobile/,
  Android: /Android/,
  iOS: /iPhone|iPad|iPod/,
  iPhone: /iPhone/,
  isAppleDevice: /iPad|iPhone|iPod|Mac OS X/,
  isBot:
    /Baiduspider|Sogou\s\w+\sspider|Yisouspider|Googlebot|Bingbot|360Spider/,
  Tablet: /(iPad|Tablet)|(Android.*Chrome(?!.*Mobile))/,
  UC: /UCBrowser/,
  Sogou: /Sogou/,
  Qihoo: /QHBrowser/,
  Baidu: /baidubrowser/,
  BaiduApp: /baidubox/,
  Safari: /^((?!chrome|android).)*safari/i,
  GoogleBot: /Googlebot/,
  DingTalk: /DingTalk/, // 钉钉
  AlipayClient: /AlipayClient/, // 支付宝
  NewsArticle: /NewsArticle/, // 今日头条
  pregnancy: /(pregnancy|AliUnionMall)/, // 宝宝树孕育
  wxMiniProgram: /(m|M)iniProgram/ // 微信小程序
};

export function UAParser(ua) {
  ua = ua || window.navigator.userAgent;
  const userAgent = Object.keys(RegExpMap).reduce((total, item) => {
    total[item] = RegExpMap[item].test(ua);
    return total;
  }, {});
  if (document.referrer.indexOf('nativeapp.babytree.com') > -1) {
    userAgent.pregnancy = true;
  }
  if (
    /MSIE\s(\d+)\.(\d+)/i.exec(ua) ||
    /Trident.*rv[.:\s](\d+)\.(\d+)/i.exec(ua)
  ) {
    userAgent.IE = true;
  }
  if (userAgent.pregnancy && !userAgent.iPhone && !userAgent.Android) {
    const query = queryStringToObject(window.location.search);
    const clientType = query['babytree-client-type'];
    userAgent.iPhone = clientType == 'ios' ? true : false;
    userAgent.Android = clientType == 'android' ? true : false;
  }
  if (userAgent.iPhone && window.screen.height >= 812) {
    userAgent.iPhoneX = true;
  }
  userAgent.origin = ua;
  return userAgent;
}

// https://github.com/lodash/lodash/blob/master/isObject.js
export function isObject(value) {
  const type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

// 函数防抖和节流 : https://www.jianshu.com/p/c8b86b09daf0
// https://github.com/lodash/lodash/blob/master/debounce.js
export function debounce(func, wait, options) {
  let lastArgs, lastThis, maxWait, result, timerId, lastCallTime;

  let lastInvokeTime = 0;
  let leading = false;
  let maxing = false;
  let trailing = true;

  // Bypass `requestAnimationFrame` by explicitly setting `wait=0`.
  const useRAF =
    !wait && wait !== 0 && typeof window.requestAnimationFrame === 'function';

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }
  wait = +wait || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function startTimer(pendingFunc, wait) {
    if (useRAF) {
      window.cancelAnimationFrame(timerId);
      return window.requestAnimationFrame(pendingFunc);
    }
    return setTimeout(pendingFunc, wait);
  }

  function cancelTimer(id) {
    if (useRAF) {
      return window.cancelAnimationFrame(id);
    }
    clearTimeout(id);
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = startTimer(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (
      lastCallTime == undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxing && timeSinceLastInvoke >= maxWait)
    );
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = startTimer(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      cancelTimer(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId == undefined ? result : trailingEdge(Date.now());
  }

  function pending() {
    return timerId !== undefined;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId == undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        timerId = startTimer(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId == undefined) {
      timerId = startTimer(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;
  return debounced;
}

// https://github.com/lodash/lodash/blob/master/throttle.js
export function throttle(func, wait, options) {
  let leading = true;
  let trailing = true;

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }
  if (isObject(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    leading,
    trailing,
    maxWait: wait
  });
}

export const queryStringToObject = query => {
  const json = {};
  if (!query || typeof query !== 'string') {
    return json;
  }
  if (query.indexOf('?') == 0) {
    query = query.substr(1);
  }
  const qarr = query.split('&');
  for (const i of qarr) {
    const item = i.split('=');
    json[item[0]] = item[1];
  }
  return json;
};

export const objectToQueryString = queryParameters => {
  return queryParameters
    ? Object.entries(queryParameters).reduce(
      (queryString, [key, val], index) => {
        const symbol = queryString.length == 0 ? '?' : '&';
        queryString += typeof val === 'string' ? `${symbol}${key}=${val}` : '';
        return queryString;
      },
      ''
    )
    : '';
};

export const isPassive = function () {
  let supportsPassiveOption = false;
  try {
    addEventListener(
      'test',
      null,
      Object.defineProperty({}, 'passive', {
        get() {
          supportsPassiveOption = true;
        }
      })
    );
  } catch (e) { }
  return supportsPassiveOption;
};

export const preventDefault = function (e) {
  e.preventDefault();
};

export const http2any = function (url) {
  return url ? url.replace(/^http[s]?[:]?/, '') : null;
};

const prefetches = new Set();

export const preload = function (url) {
  if (prefetches.has(url)) {
    return;
  }

  const prefetcher = document.createElement('link');
  prefetcher.rel = 'prefetch';
  prefetcher.href = url;
  document.head.appendChild(prefetcher);

  prefetches.add(url);
};

export const transformPrice = (price = 0) => {
  const priceArr = String(Number(price)).split('.');
  return {
    getInt: () => priceArr[0],
    getFloat: () => priceArr[1] || '',
    getFloatWithDot: () => `${priceArr[1] ? `.${priceArr[1]}` : ''}`
  };
};
export const doLogin = (weixinNeedsLogin) => {
  if (!Tools.isApp()) {
    if (Tools.isWeixin() && weixinNeedsLogin) {
      if (!checkLogin()) {
        return false;
      }
    } else {
      Tools.dp();
      return false;
    }
  }
  if (!Tools.login()) {
    return false;
  };
  return true;
};
export const stopMedias = () => {
  function removeMedia() {
    let videos = document.getElementsByTagName('video')
    for (let i = 0; i < videos.length; i++) {
      videos[i].pause();
    };
    let audios = document.getElementsByTagName('audio');
    for (let i = 0; i < audios.length; i++) {
      audios[i].pause();
    };
    const elementsWithMusicOn = document.querySelectorAll('.music-on');
    elementsWithMusicOn.forEach(element => {
      element.classList.remove('music-on');
    });
  }
  if (!Tools.isApp()) {
    document.onvisibilitychange = function () {
      if (document.visibilityState == 'visible') {
      } else {
        removeMedia();
      }
    };
  } else {
    window.webviewDisappear = function () {
      removeMedia();
    };
  }
};